import type { RcFile } from '@honestica/ui-kit/src';
import { useFeatureFlags } from '@launcher/context/FeatureFlags.context';
import { analysisFallbackPoller } from '@services/analysisFallbackPoller.service';
import {
  clearBatchUploadAnalysisState,
  notifyRootDocumentTerminal,
} from '@services/analytics/batchUploadAnalysisTracker';
import { clearFileImportCompletedState } from '@services/analytics/fileImportCompletedTracker';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  isTerminalUploadStatus,
  type UploadStatus,
} from '../../common/document-import/uploadStatus';

export type { UploadStatus };

export interface UploadError {
  code: string;
  params?: Record<string, string | number>;
  httpDetails?: {
    statusCode?: number;
    message?: string;
    response?: unknown;
  };
}

export interface UploadState {
  file: RcFile;
  status: UploadStatus;
  error?: UploadError;
  documentId?: string;
  children?: UploadState[];
}

type DocumentsMap = Map<string, UploadState>;

interface DocumentProcessingState {
  documents: Map<string, UploadState>;
  childToParentIndex: Map<string, string>;
}

type DocumentProcessingAction =
  | { type: 'ADD_DOCUMENTS'; payload: RcFile[] }
  | { type: 'UPDATE_STATUS'; payload: { id: string; status: UploadStatus; error?: UploadError } }
  | { type: 'REPLACE_ID'; payload: { oldId: string; newId: string } }
  | { type: 'REMOVE_DOCUMENT'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'REGISTER_DOCUMENT_BURST'; payload: { parentId: string; childrenIds: string[] } }
  | { type: 'ADD_PUBLIPOSTAGE_DOCUMENT'; payload: { parentId: string; childId: string } };

export interface DocumentProcessingContextValue {
  documents: DocumentsMap;
  childToParentIndex: Map<string, string>;
  addDocuments: (files: RcFile[]) => void;
  updateStatus: (id: string, status: UploadStatus, error?: UploadError) => void;
  replaceId: (oldId: string, newId: string) => void;
  removeDocument: (id: string) => void;
  clearAll: () => void;
  registerDocumentBurst: (parentId: string, childrenIds: string[]) => void;
  addPublipostageDocument: (parentId: string, childId: string) => void;
}

const DocumentProcessingContext = createContext<DocumentProcessingContextValue | null>(null);

function documentsReducer(
  state: DocumentProcessingState,
  action: DocumentProcessingAction,
): DocumentProcessingState {
  switch (action.type) {
    case 'ADD_DOCUMENTS': {
      const newDocuments = new Map(state.documents);

      action.payload.forEach((file) => {
        const id = file.uid;
        const uploadState: UploadState = {
          file,
          status: 'PENDING',
        };

        const parentId = state.childToParentIndex.get(id);

        if (parentId) {
          const parent = newDocuments.get(parentId);
          if (parent) {
            parent.children = parent.children || [];
            parent.children.push(uploadState);
          }
        } else {
          newDocuments.set(id, uploadState);
        }
      });

      return { ...state, documents: newDocuments };
    }

    case 'UPDATE_STATUS': {
      const { id, status, error } = action.payload;
      const newDocuments = new Map(state.documents);

      const parentId = state.childToParentIndex.get(id);

      if (parentId) {
        const parent = newDocuments.get(parentId);
        if (parent?.children) {
          const childIndex = parent.children.findIndex(
            (c) => c.documentId === id || c.file.uid === id,
          );
          if (childIndex !== -1) {
            parent.children[childIndex] = {
              ...parent.children[childIndex],
              status,
              error,
            };

            const allChildrenDone = parent.children.every((child) =>
              isTerminalUploadStatus(child.status),
            );

            if (allChildrenDone) {
              parent.status = 'TERMINATED';
            }
          }
        }
      } else {
        const existing = newDocuments.get(id);
        if (existing) {
          // If this document has children (e.g. publipostage or document burst),
          // don't directly override its status — let the allChildrenDone check
          // in the parent branch manage it when children statuses are updated.
          if (existing.children && existing.children.length > 0) {
            return state;
          }
          newDocuments.set(id, { ...existing, status, error });
        }
      }

      return { ...state, documents: newDocuments };
    }

    case 'REPLACE_ID': {
      const { oldId, newId } = action.payload;
      const newDocuments = new Map(state.documents);
      const newChildToParentIndex = new Map(state.childToParentIndex);

      const parentId = state.childToParentIndex.get(oldId);

      if (parentId) {
        const parent = newDocuments.get(parentId);
        if (parent?.children) {
          const childIndex = parent.children.findIndex((c) => c.file.uid === oldId);
          if (childIndex !== -1) {
            parent.children[childIndex] = {
              ...parent.children[childIndex],
              documentId: newId,
            };
          }
        }

        newChildToParentIndex.delete(oldId);
        newChildToParentIndex.set(newId, parentId);
      } else {
        const existingState = newDocuments.get(oldId);
        if (existingState) {
          newDocuments.delete(oldId);
          newDocuments.set(newId, { ...existingState, documentId: newId });
        }
      }

      return {
        documents: newDocuments,
        childToParentIndex: newChildToParentIndex,
      };
    }

    case 'REMOVE_DOCUMENT': {
      const newDocuments = new Map(state.documents);
      newDocuments.delete(action.payload);
      return { ...state, documents: newDocuments };
    }

    case 'REGISTER_DOCUMENT_BURST': {
      const { parentId, childrenIds } = action.payload;
      const newDocuments = new Map(state.documents);
      const newChildToParentIndex = new Map(state.childToParentIndex);

      const existingParent = newDocuments.get(parentId);
      if (!existingParent) {
        return state;
      }

      // The burst event carries the authoritative list of children and doubles as a catch-up for
      // Pusher events we may have missed, so rebuilding the list from it is right. Rebuilding the
      // statuses is not: a child that already reached a terminal status would be sent back to
      // ANALYZING, and nothing could ever move it forward again — its analysis is over, so no
      // further event is coming, and stopPolling has already blacklisted it in the fallback poller.
      const knownChildren = new Map(
        (existingParent.children ?? []).map((child) => [child.documentId ?? child.file.uid, child]),
      );

      const children: UploadState[] = childrenIds.map(
        (childId, index) =>
          knownChildren.get(childId) ?? {
            file: { uid: childId, name: `Document ${index + 1}` } as RcFile,
            status: 'ANALYZING' as UploadStatus,
            documentId: childId,
          },
      );

      const allChildrenDone = children.every((child) => isTerminalUploadStatus(child.status));

      newDocuments.set(parentId, {
        ...existingParent,
        status: allChildrenDone ? 'TERMINATED' : 'ANALYZING',
        documentId: parentId,
        children,
      });

      childrenIds.forEach((childId) => {
        newChildToParentIndex.set(childId, parentId);
      });

      return {
        documents: newDocuments,
        childToParentIndex: newChildToParentIndex,
      };
    }

    case 'ADD_PUBLIPOSTAGE_DOCUMENT': {
      const { parentId, childId } = action.payload;
      const newDocuments = new Map(state.documents);
      const newChildToParentIndex = new Map(state.childToParentIndex);

      const existingParent = newDocuments.get(parentId);
      if (!existingParent) {
        return state;
      }

      if (newChildToParentIndex.has(childId)) {
        return state;
      }

      const newChild: UploadState = {
        file: {
          uid: childId,
          name: `Document ${(existingParent.children?.length || 0) + 1}`,
        } as RcFile,
        status: 'ANALYZING' as UploadStatus,
        documentId: childId,
      };

      newDocuments.set(parentId, {
        ...existingParent,
        status: 'ANALYZING' as UploadStatus,
        children: [...(existingParent.children || []), newChild],
      });

      newChildToParentIndex.set(childId, parentId);

      return {
        documents: newDocuments,
        childToParentIndex: newChildToParentIndex,
      };
    }

    case 'CLEAR_ALL': {
      return {
        documents: new Map<string, UploadState>(),
        childToParentIndex: new Map<string, string>(),
      };
    }

    default:
      return state;
  }
}

function useBatchUploadAnalysisObserver(documents: Map<string, UploadState>, enabled: boolean) {
  const terminalRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) return;

    documents.forEach((doc, rootId) => {
      const isTerminal = isTerminalUploadStatus(doc.status);
      const wasTerminal = terminalRef.current.has(rootId);

      if (isTerminal && !wasTerminal && doc.documentId) {
        notifyRootDocumentTerminal(doc.documentId);
      }

      if (isTerminal) {
        terminalRef.current.add(rootId);
      } else {
        terminalRef.current.delete(rootId);
      }
    });
  }, [documents, enabled]);
}

function useAnalysisPollingObserver(documents: Map<string, UploadState>) {
  const analyzingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const nowAnalyzing = new Set<string>();

    const observe = (doc: UploadState, id: string) => {
      const documentId = doc.documentId ?? id;
      const wasAnalyzing = analyzingRef.current.has(documentId);
      const isAnalyzing = doc.status === 'ANALYZING';

      if (!wasAnalyzing && isAnalyzing) {
        analysisFallbackPoller.startPolling(documentId);
      }

      if (wasAnalyzing && isTerminalUploadStatus(doc.status)) {
        analysisFallbackPoller.stopPolling(documentId);
      }

      if (isAnalyzing) {
        nowAnalyzing.add(documentId);
      }

      doc.children?.forEach((child) => observe(child, child.file.uid));
    };

    documents.forEach(observe);

    analyzingRef.current = nowAnalyzing;
  }, [documents]);
}

export function DocumentProcessingProvider({ children }: PropsWithChildren) {
  const [state, dispatch] = useReducer(documentsReducer, {
    documents: new Map<string, UploadState>(),
    // Fast access to parent from a child
    childToParentIndex: new Map<string, string>(),
  });

  const reduxDispatch = useDispatch();
  const featureFlags = useFeatureFlags();
  const newImportAnalyzeFlowEnabled = Boolean(featureFlags['enable-new-import-analyze-flow']);

  useEffect(() => {
    analysisFallbackPoller.init({ dispatch: reduxDispatch });
    return () => analysisFallbackPoller.destroy();
  }, [reduxDispatch]);

  useBatchUploadAnalysisObserver(state.documents, newImportAnalyzeFlowEnabled);
  useAnalysisPollingObserver(state.documents);

  const addDocuments = useCallback((files: RcFile[]) => {
    dispatch({ type: 'ADD_DOCUMENTS', payload: files });
  }, []);

  const updateStatus = useCallback((id: string, status: UploadStatus, error?: UploadError) => {
    dispatch({ type: 'UPDATE_STATUS', payload: { id, status, error } });
  }, []);

  const replaceId = useCallback((oldId: string, newId: string) => {
    dispatch({ type: 'REPLACE_ID', payload: { oldId, newId } });
  }, []);

  const removeDocument = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_DOCUMENT', payload: id });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  const registerDocumentBurst = useCallback((parentId: string, childrenIds: string[]) => {
    dispatch({ type: 'REGISTER_DOCUMENT_BURST', payload: { parentId, childrenIds } });
  }, []);

  const addPublipostageDocument = useCallback((parentId: string, childId: string) => {
    dispatch({ type: 'ADD_PUBLIPOSTAGE_DOCUMENT', payload: { parentId, childId } });
  }, []);

  const value: DocumentProcessingContextValue = {
    documents: state.documents,
    childToParentIndex: state.childToParentIndex,
    addDocuments,
    updateStatus,
    replaceId,
    removeDocument,
    clearAll,
    registerDocumentBurst,
    addPublipostageDocument,
  };

  // Expose imperative API for sagas
  useEffect(() => {
    externalAddDocuments = addDocuments;
    externalUpdateStatus = updateStatus;
    externalReplaceId = replaceId;
    externalRemoveDocument = removeDocument;
    externalClearAll = clearAll;
    externalRegisterDocumentBurst = registerDocumentBurst;
    externalAddPublipostageDocument = addPublipostageDocument;

    return () => {
      externalAddDocuments = null;
      externalUpdateStatus = null;
      externalReplaceId = null;
      externalRemoveDocument = null;
      externalClearAll = null;
      externalRegisterDocumentBurst = null;
      externalAddPublipostageDocument = null;
    };
  }, [
    addDocuments,
    updateStatus,
    replaceId,
    removeDocument,
    clearAll,
    registerDocumentBurst,
    addPublipostageDocument,
  ]);

  return (
    <DocumentProcessingContext.Provider value={value}>
      {children}
    </DocumentProcessingContext.Provider>
  );
}

export function useDocumentProcessing(): DocumentProcessingContextValue {
  const ctx = useContext(DocumentProcessingContext);
  if (!ctx) {
    throw new Error('useDocumentProcessing must be used within DocumentProcessingProvider');
  }
  return ctx;
}

// Imperative API for sagas compatibility
let externalAddDocuments: ((files: RcFile[]) => void) | null = null;
let externalUpdateStatus: ((id: string, status: UploadStatus, error?: UploadError) => void) | null =
  null;
let externalReplaceId: ((oldId: string, newId: string) => void) | null = null;
let externalRemoveDocument: ((id: string) => void) | null = null;
let externalClearAll: (() => void) | null = null;
let externalRegisterDocumentBurst: ((parentId: string, childrenIds: string[]) => void) | null =
  null;
let externalAddPublipostageDocument: ((parentId: string, childId: string) => void) | null = null;

export const DocumentProcessingController = {
  addDocuments(files: RcFile[]) {
    if (!externalAddDocuments) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalAddDocuments(files);
  },

  updateStatus(id: string, status: UploadStatus, error?: UploadError) {
    if (!externalUpdateStatus) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalUpdateStatus(id, status, error);
  },

  replaceId(oldId: string, newId: string) {
    if (!externalReplaceId) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalReplaceId(oldId, newId);
  },

  removeDocument(id: string) {
    if (!externalRemoveDocument) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalRemoveDocument(id);
  },

  clearAll() {
    clearBatchUploadAnalysisState();
    clearFileImportCompletedState();
    if (!externalClearAll) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalClearAll();
  },

  registerDocumentBurst(parentId: string, childrenIds: string[]) {
    if (!externalRegisterDocumentBurst) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalRegisterDocumentBurst(parentId, childrenIds);
  },

  addPublipostageDocument(parentId: string, childId: string) {
    if (!externalAddPublipostageDocument) {
      console.warn('DocumentProcessingProvider not mounted yet');
      return;
    }
    externalAddPublipostageDocument(parentId, childId);
  },
};
